/**
 * useDisplayStore — backward-compatibility facade over the split stores.
 *
 * As of architecture review item 5, the monolithic display store has been
 * split into three focused Pinia stores:
 *
 *   • `useLayoutStore`     Sidebar, aside, breakpoints, form-screen,
 *                          mobile menu, boxed-layout flags + the responsive
 *                          breakpoint controller.
 *   • `useChromeStore`     Theme (dark mode, preset, mode), locale, the
 *                          newsletter modal, and the force-update flag.
 *   • `useChartSyncStore`  The `syncedCharts` registry + sync/reset actions
 *                          used by DashboardPanel chart components.
 *
 * Existing consumers continue to import `useDisplayStore` and use the same
 * keys/actions as before — this facade proxies reads/writes/actions to the
 * appropriate sub-store via a setup-style Pinia definition. New code should
 * import the focused stores directly.
 */

import { computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useChartSyncStore } from './useChartSyncStore';
import { useChromeStore } from './useChromeStore';
import { useLayoutStore } from './useLayoutStore';

export const useDisplayStore = defineStore('display', () => {
  const layoutStore = useLayoutStore();
  const chromeStore = useChromeStore();
  const chartSyncStore = useChartSyncStore();

  const layoutRefs = storeToRefs(layoutStore);
  const chromeRefs = storeToRefs(chromeStore);
  const chartSyncRefs = storeToRefs(chartSyncStore);

  const getSyncValues = computed(() => chartSyncStore.getSyncValues);

  return {
    // Layout refs + actions
    ...layoutRefs,
    setSidebarOpen: layoutStore.setSidebarOpen,
    setSidebarCollapsed: layoutStore.setSidebarCollapsed,
    layoutBreakpoints: layoutStore.layoutBreakpoints,
    mobileMenuToggle: layoutStore.mobileMenuToggle,
    asideCompactToggle: layoutStore.asideCompactToggle,
    asideMobileToggle: layoutStore.asideMobileToggle,
    asideRightToggle: layoutStore.asideRightToggle,
    secondaryMenuToggle: layoutStore.secondaryMenuToggle,
    asideHiddenLayoutToggle: layoutStore.asideHiddenLayoutToggle,
    asideVisibilityToggle: layoutStore.asideVisibilityToggle,
    formScreenToggle: layoutStore.formScreenToggle,
    responsiveLayoutControl: layoutStore.responsiveLayoutControl,
    defaultSidebarCollapsed: layoutStore.defaultSidebarCollapsed,

    // Chrome refs + actions
    ...chromeRefs,
    setDarkMode: chromeStore.setDarkMode,
    newsletterModalToggle: chromeStore.newsletterModalToggle,
    setFullscreenLoaderForced: chromeStore.setFullscreenLoaderForced,
    forceUpdate: chromeStore.forceUpdate,
    defaultLocale: chromeStore.defaultLocale,
    defaultDarkMode: () => chromeStore.defaultDarkMode(),
    defaultTheme: () => chromeStore.defaultTheme(),
    setLocale: chromeStore.setLocale,
    setThemePreset: chromeStore.setThemePreset,
    setThemeMode: chromeStore.setThemeMode,
    toggleThemeMode: () => chromeStore.toggleThemeMode(),

    // Chart-sync refs + actions
    ...chartSyncRefs,
    resetSyncedCharts: chartSyncStore.resetSyncedCharts,
    syncCharts: chartSyncStore.syncCharts,
    removeSyncedChart: chartSyncStore.removeSyncedChart,
    syncChart: chartSyncStore.syncChart,

    // Cross-cutting
    getSyncValues,
  };
});
